import { createPage, getAllComponents, type CurvaDocument, type Page } from "@/document/model/document";
import type { DocumentStyle, StyleBindings } from "@/document/model/documentStyles";
import type { ComponentNode, SceneNode } from "@/document/model/nodes";
import { cloneSceneNodeWithFreshIds } from "@/document/operations/cloneSceneNode";
import { createId } from "@/lib/ids";

const IMPORT_COMPONENT_X = 96;
const IMPORT_COMPONENT_Y = 96;
const IMPORT_COMPONENT_GAP = 80;

export interface PreparedComponentImport {
  page: Page;
  componentIds: string[];
  styles: DocumentStyle[];
}

export function prepareComponentImport(
  currentDocument: CurvaDocument,
  importedDocument: CurvaDocument,
  pageName: string,
): PreparedComponentImport {
  const importedComponents = getAllComponents(importedDocument);
  const styleIdMap = importedStyleIdMap(currentDocument, importedDocument, importedComponents);
  const styles = importedStylesForMap(importedDocument, styleIdMap);

  let y = IMPORT_COMPONENT_Y;
  const clonedComponents = importedComponents.map(component => {
    const offset = { x: IMPORT_COMPONENT_X - component.bounds.x, y: y - component.bounds.y };
    const clone = cloneSceneNodeWithFreshIds(component, { offset, renameRoot: false }) as ComponentNode;
    remapStyleBindingsInNode(clone, styleIdMap);
    y += Math.max(1, clone.bounds.height) + IMPORT_COMPONENT_GAP;
    return clone;
  });

  return {
    page: {
      ...createPage(pageName),
      children: clonedComponents,
    },
    componentIds: clonedComponents.map(component => component.id),
    styles,
  };
}

function importedStyleIdMap(
  currentDocument: CurvaDocument,
  importedDocument: CurvaDocument,
  components: ComponentNode[],
): Map<string, string> {
  const usedImportedStyleIds = new Set<string>();
  for (const component of components) {
    collectStyleBindingIds(component, usedImportedStyleIds);
  }

  const currentStyleIds = new Set(currentDocument.styles.items.map(style => style.id));
  const importedStyleIds = new Set(importedDocument.styles.items.map(style => style.id));
  const map = new Map<string, string>();
  for (const styleId of usedImportedStyleIds) {
    if (!importedStyleIds.has(styleId)) {
      continue;
    }
    map.set(styleId, currentStyleIds.has(styleId) ? createId("style") : styleId);
  }
  return map;
}

function importedStylesForMap(importedDocument: CurvaDocument, styleIdMap: Map<string, string>): DocumentStyle[] {
  const importedStyles = new Map(importedDocument.styles.items.map(style => [style.id, style]));
  return [...styleIdMap.entries()].flatMap(([fromId, toId]) => {
    const style = importedStyles.get(fromId);
    return style ? [{ ...structuredClone(style), id: toId }] : [];
  });
}

function collectStyleBindingIds(node: SceneNode, ids: Set<string>): void {
  for (const styleId of Object.values(node.styleBindings ?? {})) {
    if (styleId) {
      ids.add(styleId);
    }
  }

  for (const child of childNodes(node)) {
    collectStyleBindingIds(child, ids);
  }
}

function remapStyleBindingsInNode(node: SceneNode, styleIdMap: Map<string, string>): void {
  if (node.styleBindings) {
    node.styleBindings = remapStyleBindings(node.styleBindings, styleIdMap);
  }

  for (const child of childNodes(node)) {
    remapStyleBindingsInNode(child, styleIdMap);
  }
}

function remapStyleBindings(bindings: StyleBindings, styleIdMap: Map<string, string>): StyleBindings | undefined {
  const next = Object.fromEntries(
    Object.entries(bindings).map(([key, value]) => [key, value ? styleIdMap.get(value) ?? value : value]),
  ) as StyleBindings;
  return Object.values(next).some(Boolean) ? next : undefined;
}

function childNodes(node: SceneNode): SceneNode[] {
  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    return node.children;
  }
  if (node.type === "boolean") {
    return node.sourceNodes;
  }
  return [];
}
